import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAttempt } from '../context/AttemptContext';
import TopNav from '../components/TopNav.jsx';
import WritingReportView from '../components/WritingReportView.jsx';
import { getAttempt, getMyAttempts } from '../api/exams';

export default function Results() {
  const navigate = useNavigate();
  const { attemptId, wRes, sRes, rdRes, examId, reset } = useAttempt();
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [activeAttempt, setActiveAttempt] = useState(null);
  const [showSubmittedAnswers, setShowSubmittedAnswers] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));

  const theme = useMemo(() => ({
    pageBg: '#F9FAFB',
    pageBgSoft: '#EEF2FF',
    surface: '#ffffff',
    surfaceAlt: '#FFFFFF',
    surfaceSoft: '#F8FAFC',
    border: '#E5E7EB',
    text: '#111827',
    body: '#374151',
    muted: '#9CA3AF',
    accent: '#4F46E5',
    shadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  }), []);

  useEffect(() => {
    let cancelled = false;
    const loadRecentReports = async () => {
      try {
        const attempts = await getMyAttempts();
        const ids = [...new Set([...(attempts || []).map((attempt) => attempt.id), ...(attemptId ? [attemptId] : [])])];
        const details = await Promise.all(ids.slice(0, 12).map((id) => getAttempt(id).catch(() => null)));
        if (!cancelled) {
          const nextAttempts = details.filter(Boolean);
          setRecentAttempts(nextAttempts);
          setActiveAttempt((current) => current || nextAttempts.find((attempt) => attempt.writing_report?.mark_status === 'done') || nextAttempts[0] || null);
        }
      } catch {
        if (!cancelled) {
          setRecentAttempts([]);
          if (attemptId) {
            const detail = await getAttempt(attemptId).catch(() => null);
            if (detail) {
              setActiveAttempt(detail);
            }
          }
        }
      } finally {
        if (!cancelled) setReportsLoading(false);
      }
    };
    loadRecentReports();
    return () => { cancelled = true; };
  }, [attemptId]);

  useEffect(() => {
    setShowSubmittedAnswers(false);
  }, [activeAttempt?.id]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const calcOverall = () => {
    if (!wRes || !sRes) return null;
    const writingMax = wRes.max_total || 20;
    const avg = (wRes.total / writingMax + sRes.total / 15) / 2;
    if (avg >= 0.75) return { grade: 'B1+', level: 'Excellent — Ready to Pass', verdict: 'مستواك ممتاز! أنت جاهز لاجتياز اختبار القبول.' };
    if (avg >= 0.55) return { grade: 'B1', level: 'Good — Almost Ready', verdict: 'مستوى جيد. مع بعض التدريب الإضافي ستكون جاهزاً تماماً.' };
    return { grade: 'A2', level: 'Keep Practicing', verdict: 'تحتاج مزيداً من التدريب. استمر وستصل!' };
  };

  const overall = calcOverall();
  const isTablet = viewportWidth < 1024;
  const isMobile = viewportWidth < 768;
  const reportListMaxHeight = recentAttempts.length > 5 ? (isMobile ? '540px' : '690px') : 'none';
  const formatAttemptDate = (value) => {
    if (!value) return 'Date unavailable';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Date unavailable';
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAttemptMeta = (attempt) => {
    if (attempt?.writing_report) {
      const status = attempt.writing_report.mark_status || 'marking';
      if (status === 'done') return { label: 'Ready', bg: '#EEF2FF', color: '#4F46E5', summary: `${attempt.writing_report.total ?? '—'}/${attempt.writing_report.max_total ?? '—'}` };
      if (status === 'failed') return { label: 'Failed', bg: '#FEF2F2', color: '#DC2626', summary: 'Marking failed' };
      return { label: 'Marking', bg: '#FFFBEB', color: '#B45309', summary: 'Awaiting result' };
    }
    if (attempt?.speaking_responses?.length) {
      const speaking = attempt.speaking_responses[0];
      return { label: 'Speaking', bg: '#ECFDF5', color: '#059669', summary: speaking?.total != null ? `${speaking.total}/15` : 'Awaiting result' };
    }
    if (attempt?.reading_responses?.length) {
      const reading = attempt.reading_responses[0];
      return { label: 'Reading', bg: '#F5F3FF', color: '#7C3AED', summary: reading?.total_score != null ? `${reading.total_score}/${reading.max_score}` : 'Awaiting result' };
    }
    return { label: 'Attempt', bg: '#F3F4F6', color: '#374151', summary: 'No report yet' };
  };

  const writingReport = activeAttempt?.writing_report || null;
  const backTo = examId ? `/hub/${examId}` : '/platform';
  const homeAction = (
    <button
      type="button"
      onClick={() => navigate('/platform')}
      style={{
        height: 40,
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,.18)',
        background: 'rgba(255,255,255,.10)',
        color: '#ffffff',
        padding: '0 16px',
        fontWeight: 800,
        cursor: 'pointer',
        boxShadow: '0 10px 22px rgba(15,23,42,.12)',
      }}
    >
      الرئيسية
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, ${theme.pageBgSoft} 0%, ${theme.pageBg} 100%)`, color: theme.text }}>
      <TopNav backTo={backTo} backLabel={examId ? 'الاختبار' : 'المنصة'} title="النتائج" leadingAction={homeAction} />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '20px 16px 32px' : '28px 20px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isTablet ? '1fr' : '320px minmax(0, 1fr)', gap: '20px' }}>
          <aside style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '22px', boxShadow: theme.shadow, padding: '18px', display: 'flex', flexDirection: 'column', minHeight: isTablet ? 'auto' : '720px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', color: theme.muted, textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 800, marginBottom: '8px' }}>
                  Report List
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: theme.text }}>Your Attempts</div>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, maxHeight: reportListMaxHeight, overflowY: 'auto', paddingRight: '6px' }}>
              {reportsLoading ? (
                <div style={{ color: theme.body, fontSize: '14px' }}>Loading reports...</div>
              ) : recentAttempts.length === 0 ? (
                <div style={{ borderRadius: '18px', border: `1px dashed ${theme.border}`, padding: '20px', background: theme.surfaceSoft, color: theme.body }}>
                  No recent reports yet.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {recentAttempts.map((attempt) => {
                    const meta = getAttemptMeta(attempt);
                    const isActive = activeAttempt?.id === attempt.id;
                    return (
                      <button
                        key={attempt.id}
                        onClick={() => setActiveAttempt(attempt)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          borderRadius: '18px',
                          border: `1px solid ${isActive ? theme.accent : theme.border}`,
                          background: isActive ? theme.pageBgSoft : theme.surfaceAlt,
                          padding: '16px',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '10px' }}>
                          <div style={{ fontSize: '15px', fontWeight: 700, color: theme.text }}>
                            {attempt.exam_title || 'Writing Test'}
                          </div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: '999px', background: meta.bg, color: meta.color, fontSize: '12px', fontWeight: 800 }}>
                            {meta.label}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: theme.muted, marginBottom: '12px' }}>
                          {formatAttemptDate(attempt.completed_at || attempt.started_at)}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', color: theme.body, fontSize: '13px', flexWrap: 'wrap' }}>
                          <span>{meta.summary}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>

          <main style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '22px', boxShadow: theme.shadow, padding: isMobile ? '18px' : '24px' }}>
            {!activeAttempt ? (
              <div style={{ color: theme.body, fontSize: '15px' }}>Select a report to view the details.</div>
            ) : writingReport?.mark_status === 'done' ? (
              <WritingReportView
                report={writingReport}
                responses={activeAttempt?.writing_responses || []}
                theme={theme}
                isMobile={isMobile}
                isTablet={isTablet}
                showSubmittedAnswers={showSubmittedAnswers}
                onToggleSubmittedAnswers={() => setShowSubmittedAnswers((open) => !open)}
              />
            ) : (
              <div style={{ color: theme.body, fontSize: '15px' }}>
                No writing report is available for this attempt yet.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              <button
                className="btn btn-ghost"
                style={{ flex: 1 }}
                onClick={() => { navigate(`/hub/${examId}`); reset(); }}
              >
                ← Back to Hub
              </button>
              <button
                className="btn btn-gold"
                style={{ flex: 1 }}
                onClick={() => { reset(); navigate(`/exam/${examId}`); }}
              >
                Try Again →
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
